import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { LocalPresenceNumber } from "@/data/localPresence";

interface DBRow {
  id: string;
  organization_id: string;
  phone_number: string;
  area_code: string;
  city: string;
  state: string;
  active: boolean;
  provider_id: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

function toNumber(row: DBRow): LocalPresenceNumber {
  return {
    id: row.id,
    phoneNumber: row.phone_number,
    areaCode: row.area_code,
    city: row.city,
    state: row.state,
    active: row.active,
    providerId: row.provider_id ?? "",
    isDefault: row.is_default,
  };
}

export const LocalPresenceService = {
  async getAll(): Promise<LocalPresenceNumber[]> {
    const { data, error } = await supabase
      .from("local_presence_numbers")
      .select("*")
      .order("is_default", { ascending: false });

    if (error) throw error;
    return (data as DBRow[]).map(toNumber);
  },

  async create(num: Omit<LocalPresenceNumber, "id">): Promise<LocalPresenceNumber> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("local_presence_numbers")
      .insert({
        organization_id: organizationId,
        phone_number: num.phoneNumber,
        area_code: num.areaCode,
        city: num.city,
        state: num.state,
        active: num.active,
        provider_id: num.providerId || null,
        is_default: num.isDefault,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return toNumber(data as DBRow);
  },

  async update(id: string, changes: Partial<LocalPresenceNumber>): Promise<void> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (changes.phoneNumber !== undefined) payload.phone_number = changes.phoneNumber;
    if (changes.areaCode !== undefined) payload.area_code = changes.areaCode;
    if (changes.city !== undefined) payload.city = changes.city;
    if (changes.state !== undefined) payload.state = changes.state;
    if (changes.active !== undefined) payload.active = changes.active;
    if (changes.providerId !== undefined) payload.provider_id = changes.providerId || null;
    if (changes.isDefault !== undefined) payload.is_default = changes.isDefault;

    const { error } = await supabase
      .from("local_presence_numbers")
      .update(payload)
      .eq("id", id);

    if (error) throw error;
  },

  async setDefault(id: string): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    // Clear all defaults for this org first, then set the new one
    await supabase
      .from("local_presence_numbers")
      .update({ is_default: false, updated_at: new Date().toISOString() })
      .eq("organization_id", organizationId);

    const { error } = await supabase
      .from("local_presence_numbers")
      .update({ is_default: true, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("local_presence_numbers")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
